import { type Dispatch, type SetStateAction } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import PaymentModeCards from '@/components/event-form/PaymentModeCards'
import PurchasableItemsCard from '@/components/event-form/PurchasableItemsCard'
import type { EventFormEventData, FieldError, PurchasableItemRowState } from '@/components/event-form/shared'

// Event-only ticketing surface. `native` shows the built-in tier builder; `external`
// shows the linked page (stored in `ticket_link`) + an optional "Tickets from" price.
export default function TicketingPaymentsSection({
  value,
  onChange,
  rows,
  setRows,
  stripePaymentReady,
  fieldError,
  showNativeTiers = true,
  showExternalPrice = true,
}: {
  value: EventFormEventData
  onChange: (next: EventFormEventData) => void
  rows: PurchasableItemRowState[]
  setRows: Dispatch<SetStateAction<PurchasableItemRowState[]>>
  stripePaymentReady: boolean
  fieldError: FieldError
  showNativeTiers?: boolean
  showExternalPrice?: boolean
}) {
  const isExternal = value.payment_mode === 'external'

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Ticketing &amp; payments</CardTitle>
        </CardHeader>
        <CardContent>
          <PaymentModeCards
            kind="event"
            mode={value.payment_mode}
            onModeChange={(mode) => onChange({ ...value, payment_mode: mode })}
            externalUrl={value.ticket_link}
            onExternalUrlChange={(url) => onChange({ ...value, ticket_link: url })}
            urlError={fieldError('ticket_link')}
          />
          {isExternal && showExternalPrice && (
            <div className="mt-4 space-y-2">
              <Label htmlFor="starting_ticket_price">Tickets from (optional)</Label>
              <Input
                id="starting_ticket_price"
                type="number"
                step="0.01"
                min="0"
                value={value.starting_ticket_price}
                onChange={(event) => onChange({ ...value, starting_ticket_price: event.target.value })}
              />
              <p className="text-xs text-muted-foreground">Shown as &ldquo;Starting at&rdquo; on your event page.</p>
            </div>
          )}
        </CardContent>
      </Card>
      {!isExternal && showNativeTiers && (
        <PurchasableItemsCard
          kind="ticket"
          title="Tickets"
          description="Add ticket tiers. Guests see all active tiers at checkout."
          rows={rows.filter((row) => row.kind === 'ticket')}
          setAllRows={setRows}
          rowLabel="Tier"
          ctaLabel="Add tier"
          namePlaceholder="General Admission"
          descriptionPlaceholder="- Reserved seating"
          removedNoun="Ticket"
          stripePaymentReady={stripePaymentReady}
          stripeBlurb="Connect Stripe to sell tickets through Gala Season."
          fieldError={fieldError}
          allowFree
        />
      )}
    </div>
  )
}
